/**
 * Befundzeile der Sektions-Detailseite (Audit-Paket C5, 30.10.2026): ein Satz unter dem Titel, aus denselben Vergleichen
 * wie die ▲/▼ der Kacheln „Der Termin" (`abstandTypisch`, Schwellen 5 % bzw. 2 Prozentpunkte). Nur Größen, deren
 * „typisch" nicht null ist (unter 5 Terminen oder ohne Abruf gibt es keinen Vergleich — und keinen Satz dazu).
 * Muster: „Besser als typisch: Anmeldungen +20 %, Anfos +37 %, Erwarteter Wert +33 %; Show-up etwa typisch. Zahlen
 * offen bis 10.11." Rein, DOM-frei.
 */
import { abstandTypisch, type Abstand } from '../webinare-produkte/befunde';
import type { DetailKennzahlen, TypischDetail } from '../../../helpers/auswertungenData';

export interface DetailBefundEingabe {
  registrations: number;
  orders: number;
  /** Show-up des Termins (null bei Messlücke u. ä. — dann kein Vergleich). */
  show_up: number | null;
  wert_summe_fest: number | null;
  wert_je_1k_fest: number | null;
  zahlen_offen_bis: string | null;
}

type Groesse = { name: string; abstand: Abstand | null };

const tm = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
const liste = (namen: string[]) => (namen.length <= 1 ? namen.join('') : `${namen.slice(0, -1).join(', ')} und ${namen[namen.length - 1]}`);

/** Eingabe aus den Daten der Detailseite; ohne Kennzahlen (`d`) nur Anmeldungen und Anfos. */
export function detailBefundEingabe(e: { registrations: number; orders: number }, d: DetailKennzahlen | null): DetailBefundEingabe {
  return {
    registrations: e.registrations,
    orders: e.orders,
    show_up: d?.show_up && !d.show_up.grund ? d.show_up.show_up : null,
    wert_summe_fest: d ? d.wert_summe_fest : null,
    wert_je_1k_fest: d?.wert_je_1k_fest ?? null,
    zahlen_offen_bis: d?.zahlen_offen_bis ?? null,
  };
}

/** Der Satz, oder null, wenn keine einzige Größe ein „typisch" hat. */
export function detailBefund(x: DetailBefundEingabe, t: TypischDetail | null | undefined): string | null {
  if (!t) return null;
  const groessen: Groesse[] = [
    { name: 'Anmeldungen', abstand: abstandTypisch(x.registrations, t.registrations) },
    { name: 'Show-up', abstand: abstandTypisch(x.show_up, t.show_up, 'pp') },
    { name: 'Anfos', abstand: abstandTypisch(x.orders, t.anfos) },
    { name: 'Erwarteter Wert', abstand: abstandTypisch(x.wert_summe_fest, t.wert_summe_fest) },
    { name: 'Erwarteter Wert je 1.000 Kontakte', abstand: abstandTypisch(x.wert_je_1k_fest, t.value_per_1k) },
  ].filter((g) => g.abstand !== null);
  if (!groessen.length) return null;
  const mit = (r: Abstand['richtung']) => groessen.filter((g) => g.abstand?.richtung === r);
  const teile: string[] = [];
  const besser = mit('up');
  const schwaecher = mit('down');
  const gleich = mit('gleich');
  if (besser.length) teile.push(`besser als typisch: ${besser.map((g) => `${g.name} ${g.abstand?.text}`).join(', ')}`);
  if (schwaecher.length) teile.push(`schwächer als typisch: ${schwaecher.map((g) => `${g.name} ${g.abstand?.text}`).join(', ')}`);
  if (gleich.length) teile.push(`${liste(gleich.map((g) => g.name))} etwa typisch`);
  const satz = teile.join('; ');
  const offen = x.zahlen_offen_bis ? ` Zahlen offen bis ${tm(x.zahlen_offen_bis)} — Anfos und Erwarteter Wert wachsen noch.` : '';
  return `${satz.charAt(0).toUpperCase()}${satz.slice(1)}.${offen}`;
}
